/**
 * @module composables/usePolling
 *
 * Generic polling composable that executes a callback function at a
 * regular interval. Used by list views (Latest, Episodes, Playlist)
 * to periodically fetch download status updates from the server.
 *
 * Features:
 * - Double-start guard prevents duplicate intervals
 * - Async callbacks are properly awaited
 * - Errors are caught and logged without breaking the polling loop
 * - Auto-cleanup on component unmount via onUnmounted
 *
 * @example
 * const { startPolling, stopPolling } = usePolling(fetchStatus, 5000);
 * onMounted(() => startPolling());
 * onBeforeRouteLeave(() => stopPolling());
 */

// composables/usePolling.js
import { ref, onUnmounted } from "vue";

/**
 * Create a polling composable that runs a callback at a fixed interval.
 *
 * @param {Function} callback - Async function to call on each poll tick
 * @param {number} [interval=5000] - Polling interval in milliseconds
 * @returns {{ startPolling: Function, stopPolling: Function, isPolling: import('vue').Ref<boolean> }}
 */
export function usePolling(callback, interval = 5000) {
  /** The setInterval ID, or null when not polling */
  const pollInterval = ref(null);

  /** Whether polling is currently active */
  const isPolling = ref(false);

  /**
   * Start the polling loop. Optionally executes the callback immediately
   * before starting the interval.
   *
   * @param {boolean} [immediate=true] - Whether to run the callback
   *   immediately before starting the interval timer
   */
  const startPolling = async (immediate = true) => {
    // Guard: prevent starting a second polling loop if one is already active
    if (isPolling.value) return;

    isPolling.value = true;

    // Execute the callback immediately on start (before the first interval tick)
    if (immediate) {
      try {
        await callback();
      } catch (e) {
        console.error("Polling error:", e);
      }
    }

    // Set up the recurring interval
    pollInterval.value = setInterval(async () => {
      try {
        await callback();
      } catch (e) {
        // Errors are caught and logged to prevent breaking the polling loop.
        // The next tick will still fire even if this one failed.
        console.error("Polling error:", e);
      }
    }, interval);
  };

  /**
   * Stop the polling loop and clean up the interval timer.
   * Safe to call even if polling is not active (no-op).
   */
  const stopPolling = () => {
    if (pollInterval.value) {
      clearInterval(pollInterval.value);
      pollInterval.value = null; // Use null, not delete (Vue ref pattern)
    }
    isPolling.value = false;
  };

  // Automatically stop polling when the component using this composable unmounts,
  // preventing orphaned intervals that would continue firing after navigation
  onUnmounted(() => stopPolling());

  return { startPolling, stopPolling, isPolling };
}
